import { useEffect, useRef, useState } from "react";
import type { Row } from "@tanstack/react-table";
import Lottie from "lottie-react";
import type { LottieRefCurrentProps } from "lottie-react";
import { t } from "@/i18n-js/instance";
import { useProgress } from "@circle-react/pages/BotBuilder/contexts/ProgressContext";
import { Typography } from "@circle-react-shared/uikit/Typography";
import loaderAnimation from "./animations/loader.json";
import type { SpaceProp } from "./queries";

interface ProgressItem {
  id: number;
  progress: number;
  total_progress: number;
  status: string;
}

export const SpaceTitleCell = ({ row }: { row: Row<SpaceProp> }) => {
  const { spaceProgresses } = useProgress();
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const lastStatusRef = useRef<string>("done");
  const hasStartedLoadingRef = useRef(false);
  const lastTotalProgressRef = useRef<number>(0);

  const [wasEverInProgress, setWasEverInProgress] = useState(false);
  const [isProgressInfoVisible, setIsProgressInfoVisible] = useState(false);
  const [shouldCollapse, setShouldCollapse] = useState(false);

  const spaceProgress = spaceProgresses.find(p => p.id === row.original?.id) as
    | ProgressItem
    | undefined;
  const progress = spaceProgress?.progress ?? 100;
  const totalProgress = spaceProgress?.total_progress ?? 100;
  const status = spaceProgress?.status ?? "done";

  const isInProgress = status !== "done" && totalProgress !== 100;
  const isEnqueued = status === "enqueued";

  useEffect(() => {
    if (isInProgress) {
      setWasEverInProgress(true);
      setIsProgressInfoVisible(true);
      setShouldCollapse(false);
      return undefined;
    }
    if (wasEverInProgress && totalProgress === 100) {
      // Hide progress info immediately
      setIsProgressInfoVisible(false);
      // When transitioning to done, play the final animation then collapse
      const timer = setTimeout(() => {
        setShouldCollapse(true);
      }, 1500); // Reduced from 1500ms to 1000ms for quicker collapse

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isInProgress, wasEverInProgress, totalProgress]);

  // Handle Lottie animation segments based on status changes
  useEffect(() => {
    if (!lottieRef.current?.animationItem) return;

    const lottie = lottieRef.current.animationItem;

    if (shouldCollapse) {
      lottie.destroy();
      return;
    }

    const hasStatusChanged = status !== lastStatusRef.current;
    const hasCompletedProgress =
      totalProgress === 100 && lastTotalProgressRef.current !== 100;

    lastStatusRef.current = status;
    lastTotalProgressRef.current = totalProgress;

    // Handle initial animation states
    if (hasStatusChanged || hasCompletedProgress) {
      if (isEnqueued && !hasStartedLoadingRef.current) {
        hasStartedLoadingRef.current = true;
        // Play from 0 to 0.50 without looping
        lottie.playSegments([0, 86], true);
      } else if (isInProgress && !lottie.loop) {
        // Play from 0.50 to 3.0 once, then loop between 3.0 and 4.70
        lottie.playSegments([86, 102], true);
        lottie.setSegment(102, 284);
        lottie.loop = true;
      } else if (totalProgress === 100 && wasEverInProgress) {
        // Play from 4.70 to end without looping
        lottie.loop = false;
        lottie.playSegments([284, 324], true);
      }
    }
  }, [
    isEnqueued,
    isInProgress,
    status,
    wasEverInProgress,
    totalProgress,
    shouldCollapse,
  ]);

  if (!wasEverInProgress && !isInProgress) {
    return (
      <div className="flex h-[20px] items-center gap-2">
        <div className="flex flex-col justify-center">
          <Typography.LabelSm weight="semibold" color="line-clamp-1">
            {row.original?.name}
          </Typography.LabelSm>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[20px] items-center gap-2">
      <div
        className={`transition-all duration-500 ease-in-out ${shouldCollapse ? "w-0" : "w-[20px]"}`}
      >
        <Lottie
          lottieRef={lottieRef}
          animationData={loaderAnimation}
          autoplay={false}
          loop={false}
          style={{ width: 16, height: 16 }}
        />
      </div>
      <div className="flex flex-col justify-center">
        <Typography.LabelSm weight="semibold" color="line-clamp-1">
          {row.original?.name}
        </Typography.LabelSm>
        <div
          className={`overflow-hidden transition-all duration-500 ease-in-out ${isProgressInfoVisible && !shouldCollapse ? "max-h-6" : "max-h-0"}`}
        >
          <Typography.BodyXs>
            {t(
              `community_bot.bot_builder.knowledge_page.content_loading_states.${status}`,
            )}
            : {progress}%
          </Typography.BodyXs>
        </div>
      </div>
    </div>
  );
};
